/* GASSISA
========================================================================================
* Descripción: Modal para reimpresión de tickets de remisiones en Venta Super
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 03/05/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Mayor visibilidad en datos clave de reimpresión
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { Fragment, useState, useEffect } from "react";
import { Modal, Box, Typography, Button, IconButton, CircularProgress, Select, MenuItem, FormControl, InputLabel, TableContainer, Paper } from "@mui/material";
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';

import SizeSoftXTableVentas from "../leyout/SizeSoftXTableVentas";

import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import { apiObtenerRemisionVenta, apiListadoVentasCaja } from "../../settings/ApiConfig";
import { buildTicketVentaParts, layoutTieneObjetos } from "../../settings/ticketVentaEscape";
import { imprimirTicketEnPrinterTools } from "../../settings/ticketImpresion";
import { buildTicketVentaClasicoParts } from "../../settings/ticketVentaClasico";

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
    }).format(value || 0);
};

const formatDateTable = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleString('es-MX', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export default function ModalReimpresionRemisiones(props) {
    const { openModal = false, onClose, caja } = props;
    
    const { handleOpenAlert } = useLayout();
    const { GetRequest } = useRequest();
    const { parametrosCajaForm } = useSesion();
    const { 
        listaCajas, 
        handleOntenerListaCajasRegistro 
    } = useVentas();

    const [cajaSeleccionada, setCajaSeleccionada] = useState("");
    const [fechaInicio, setFechaInicio] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
    const [fechaFin, setFechaFin] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [loadingImpresion, setLoadingImpresion] = useState(false);
    const [remisionesAgrupadas, setRemisionesAgrupadas] = useState([]);
    const [ventaSeleccionada, setVentaSeleccionada] = useState(null);

    const cajaActual = cajaSeleccionada;

    useEffect(() => {
        if (openModal) {
            handleOntenerListaCajasRegistro();
        }
    }, [openModal]);

    useEffect(() => {
        if (parametrosCajaForm?.caja && parametrosCajaForm.caja !== 'CAJA-DEFAULT') {
            setCajaSeleccionada(parametrosCajaForm.caja);
        } else if (listaCajas && listaCajas.length > 0 && !cajaSeleccionada) {
            setCajaSeleccionada(listaCajas[0]);
        }
    }, [parametrosCajaForm, listaCajas]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (openModal && cajaSeleccionada && listaCajas.length > 0) {
                handleBuscarDatos();
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [openModal, cajaSeleccionada, fechaInicio, fechaFin]);

    const handleCajaChange = (event) => {
        setCajaSeleccionada(event.target.value);
        setVentaSeleccionada(null);
    };

    const handleFechaInicioChange = (event) => {
        const dateStr = event.target.value;
        setFechaInicio(new Date(dateStr + 'T12:00:00'));
    };

    const handleFechaFinChange = (event) => {
        const dateStr = event.target.value;
        setFechaFin(new Date(dateStr + 'T12:00:00'));
    };

    const getFechaValue = (fecha) => {
        if (!fecha) return "";
        const d = new Date(fecha);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateTimeQuery = (date) => {
        if (!date) return "";
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    };

    const handleBuscarDatos = async () => {
        const caja = (cajaSeleccionada || parametrosCajaForm?.caja || "").trim();
        if (!caja || caja === 'CAJA-DEFAULT') {
            handleOpenAlert("Seleccione una caja para buscar", "warning");
            return;
        }

        setLoading(true);
        setVentaSeleccionada(null);

        try {
            const params = new URLSearchParams();
            params.append('fechainicial', formatDateTimeQuery(fechaInicio));
            params.append('fechafinal', formatDateTimeQuery(fechaFin));
            const url = `${apiListadoVentasCaja.replace('{pc}', caja)}?${params.toString()}`;
            
            const response = await GetRequest({ url });
            
            if (!response.hasError && response.result) {
                const ventasOrdenadas = (response.result || []).sort((a, b) => {
                    return String(a.remision ?? "").localeCompare(String(b.remision ?? ""));
                });
                const agrupadas = agruparVentasPorRemision(ventasOrdenadas);
                setRemisionesAgrupadas(agrupadas);
            } else {
                setRemisionesAgrupadas([]);
            }
        } catch (error) {
            handleOpenAlert("Error al buscar datos: " + error.message, "error");
            setRemisionesAgrupadas([]);
        } finally {
            setLoading(false);
        }
    };

    const agruparVentasPorRemision = (ventas) => {
        const mapa = new Map();
        
        ventas.forEach((v, index) => {
            const remision = v.remision;
            if (!remision) return;
            
            if (!mapa.has(remision)) {
                mapa.set(remision, {
                    id: index,
                    remision: remision,
                    fechaventa: v.fechaventa,
                    pc: v.pc,
                    razonsocial: v.razonsocial || "PUBLICO GENERAL",
                    totalarticulos: 0,
                    total: 0
                });
            }
            
            const entry = mapa.get(remision);
            entry.totalarticulos += Number(v.cantidadarticulo || 0);
            entry.total += Number(v.totalarticulo || 0);
        });
        
        return Array.from(mapa.values()).sort((a, b) => {
            return String(a.remision ?? "").localeCompare(String(b.remision ?? ""));
        });
    };

    const handleSelectVenta = (venta) => {
        setVentaSeleccionada(venta);
    };

    const handleDoubleClickVenta = (venta) => {
        setVentaSeleccionada(venta);
        setTimeout(() => imprimirTicketRemision(venta), 100);
    };

    const imprimirTicketRemision = async (ventaEspecifica = null) => {
        const remision = ventaEspecifica?.remision || ventaSeleccionada?.remision || remisionesAgrupadas[0]?.remision;

        if (!remision) {
            handleOpenAlert("No hay remisiones disponibles para imprimir", "warning");
            return;
        }

        setLoadingImpresion(true);

        try {
            const url = apiObtenerRemisionVenta
                .replace("{remision}", remision)
                .replace("{caja}", (cajaSeleccionada || parametrosCajaForm?.caja || "").trim());

            const response = await GetRequest({ url });

            if (response.hasError || !response.result) {
                handleOpenAlert("No se pudo obtener la remision", "error");
                return;
            }

            const { partes } = layoutTieneObjetos(parametrosCajaForm)
                ? buildTicketVentaParts({
                    dataTicket: response.result,
                    parametrosCajaForm,
                    esReimpresion: true,
                })
                : buildTicketVentaClasicoParts({
                    dataTicket: response.result,
                    parametrosCajaForm,
                    esReimpresion: true,
                });
            await imprimirTicketEnPrinterTools({ partes, parametrosCajaForm });
            handleOpenAlert("Ticket reimpreso", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir ticket: " + error.message, "error");
        } finally {
            setLoadingImpresion(false);
        }
    };
    return (
        <Modal
            open={openModal}
            onClose={onClose}
            closeAfterTransition
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '150vw',
                maxWidth: 1000,
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 3,
                borderRadius: 2,
                overflow: 'auto',
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Reimpresión de Tickets de ventas
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Caja</InputLabel>
                        <Select
                            value={cajaSeleccionada}
                            label="Caja"
                            onChange={handleCajaChange}
                        >
                            {(listaCajas || []).map((c) => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <CalendarTodayIcon fontSize="small" />
                    <TextField autoComplete="off"
                        type="date"
                        label="Fecha inicial"
                        value={getFechaValue(fechaInicio)}
                        onChange={handleFechaInicioChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 180 }}
                    />
                    <Typography>-</Typography>
                    <TextField autoComplete="off"
                        type="date"
                        label="Fecha final"
                        value={getFechaValue(fechaFin)}
                        onChange={handleFechaFinChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 180 }}
                    />
                    <Button 
                        variant="contained" 
                        startIcon={<SearchIcon />}
                        onClick={handleBuscarDatos}
                    >
                        Buscar
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: '40vh', overflow: 'auto', '& .MuiTable-root': { borderCollapse: 'collapse' }, '& .MuiTableCell-root': { padding: '2px 4px' } }}>
                        <SizeSoftXTableVentas
                            columns={[
                                { title: "Fecha", field: "fechaventa", render: (row) => formatDateTable(row.fechaventa), width: 180 },
                                { title: "Remisión", field: "remision", width: 90 },
                                { title: "Monto", field: "total", render: (row) => formatCurrency(row.total), align: "center", width: 80 },
                                { title: "Caja", field: "pc", align: "center", width: 80 },
                                { title: "Arts", field: "totalarticulos", align: "center", width: 50 },
                                { title: "Cliente", field: "razonsocial" },
                            ]}
                            data={remisionesAgrupadas}
                            id="id"
                            row={ventaSeleccionada}
                            setRow={handleSelectVenta}
                            fnDoubleClick={handleDoubleClickVenta}
                            fontSize="small"
                            rowsPerPageIntro={100}
                            disablePagination={true}
                            disableStickyHeader={false}
                        />
                    </TableContainer>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<PrintIcon />}
                        onClick={imprimirTicketRemision}
                    >
                        Imprimir Ticket
                    </Button>
                    <Button variant="outlined" onClick={onClose}>
                        Cerrar
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
